import React, { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

// SpotlightIntro props:
// - onComplete: callback when the intro finishes
// - fallbackMs: auto-reveal fallback delay in milliseconds (default 8500)
// - downwardMouseThreshold: required downward mouse movement in px to trigger reveal (default 40)
// - wheelThreshold: required wheel deltaY to trigger reveal (default 10)
// - autoRevealForReducedMotion: whether to auto-reveal immediately for users who prefer reduced motion (default true)
const SpotlightIntro = ({
  onComplete,
  fallbackMs = 8500,
  downwardMouseThreshold = 40,
  wheelThreshold = 10,
  autoRevealForReducedMotion = true,
}) => {
  const [mousePos, setMousePos] = useState({ x: -200, y: -200 })
  const [revealed, setRevealed] = useState(false)
  const [done, setDone] = useState(false)
  const [showHint, setShowHint] = useState(true)
  const hasMovedMuch = useRef(false)
  const rafRef = useRef(null)
  const targetRef = useRef({ x: -200, y: -200 })
  const prevY = useRef(null)
  const revealRef = useRef(false)

  const startReveal = useCallback(() => {
    if (revealRef.current) return
    revealRef.current = true
    setRevealed(true)
    setTimeout(() => {
      setDone(true)
      setTimeout(onComplete, 600)
    }, 900)
  }, [onComplete])

  const handleMouseMove = useCallback((e) => {
    targetRef.current = { x: e.clientX, y: e.clientY }

    // Keep a lightweight hint visible until clear downward movement is detected
    if (!hasMovedMuch.current) {
      const y = e.clientY
      if (prevY.current == null) prevY.current = y
      const dy = y - prevY.current
      prevY.current = y

      // Consider a deliberate downward move only when user moves more than the configured threshold downwards
      if (dy > downwardMouseThreshold) {
        hasMovedMuch.current = true
        setShowHint(false)
        startReveal()
      }
    }
  }, [startReveal, downwardMouseThreshold])

  useEffect(() => {
    // Disable the interactive spotlight cursor effect on touch/mobile devices.
    // This prevents the “cursor + scroll” overlay from looking bad on small screens.
    const isTouchDevice =
      (typeof window !== 'undefined' && 'ontouchstart' in window) ||
      (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) ||
      (typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(pointer: coarse)').matches)

    if (isTouchDevice) {
      // Immediately finish the spotlight phase without rendering/animating the cursor overlay.
      startReveal()
      return
    }

    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    // also listen for wheel events so scrolling moves the spotlight subtly
    const handleWheel = (e) => {
      // move the target vertically based on wheel delta to reveal content while scrolling
      const dy = e.deltaY
      const tx = Math.max(40, Math.min(window.innerWidth - 40, targetRef.current.x || window.innerWidth / 2))
      const ty = Math.max(40, Math.min(window.innerHeight - 40, (targetRef.current.y || window.innerHeight / 2) + dy * 0.6))
      targetRef.current = { x: tx, y: ty }
      // Only trigger reveal on downward scroll (deltaY > configured threshold)
      if (!hasMovedMuch.current && dy > wheelThreshold) {
        hasMovedMuch.current = true
        setShowHint(false)
        startReveal()
      }
    }
    window.addEventListener('wheel', handleWheel, { passive: true })

    // Hide hint after 2s even without movement
    const hintTimer = setTimeout(() => setShowHint(false), 2000)

    // Accessibility: respect reduced-motion and provide a long fallback reveal
    if (
      autoRevealForReducedMotion &&
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      // Skip the interactive spotlight for reduced motion users
      startReveal()
    }

    const fallbackTimer = setTimeout(() => {
      startReveal()
    }, fallbackMs)

    // Smoothly lerp visible mouse position toward the latest target
    let lastTime = Date.now()
    const tick = () => {
      const now = Date.now()
      const dt = Math.min(50, now - lastTime) / 16.6667
      lastTime = now

      setMousePos((prev) => {
        const tx = targetRef.current.x
        const ty = targetRef.current.y
        const lerp = 0.18 * dt
        const nx = prev.x + (tx - prev.x) * lerp
        const ny = prev.y + (ty - prev.y) * lerp
        return { x: nx, y: ny }
      })

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)

    const currentRaf = rafRef.current
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('wheel', handleWheel)
      clearTimeout(hintTimer)
      clearTimeout(fallbackTimer)
      if (currentRaf) cancelAnimationFrame(currentRaf)
    }
  }, [handleMouseMove, startReveal, autoRevealForReducedMotion, fallbackMs, wheelThreshold])


  if (done) return null

  const size = 200
  const clipPath = revealed
    ? `circle(150% at 50% 50%)`
    : `circle(${size}px at ${mousePos.x}px ${mousePos.y}px)`

  return (
    <motion.div
      initial={{ opacity: 1 }}
      className="fixed inset-0 z-[9998]"
    >
      {/* Dark overlay with clip-path spotlight */}
      <motion.div
        className="fixed inset-0 bg-slate-950"
        style={{
          clipPath,
          WebkitClipPath: clipPath,
          transition: revealed
            ? 'clip-path 0.9s cubic-bezier(0.22, 1, 0.36, 1), -webkit-clip-path 0.9s cubic-bezier(0.22, 1, 0.36, 1)'
            : 'none',
        }}
      >
        {/* Glowing cursor ring */}
        <div
          className="pointer-events-none absolute"
          style={{
            left: mousePos.x - 100,
            top: mousePos.y - 100,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(37,99,235,0.12) 0%, rgba(37,99,235,0.06) 40%, transparent 70%)',
          }}
        />

        {/* Inner cursor dot */}
        <div
          className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-400 shadow-[0_0_12px_rgba(37,99,235,0.6)]"
          style={{ left: mousePos.x, top: mousePos.y }}
        />

        {/* Hint text */}
        <AnimatePresence>
          {showHint && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5 }}
              className="pointer-events-none absolute select-none"
              style={{
                left: mousePos.x - 70,
                top: mousePos.y + 30,
              }}
            >
              <p className="text-xs tracking-[0.2em] text-blue-400/60 uppercase">
                Move to explore
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Center hint text when mouse hasn't moved */}
        {mousePos.x === -200 && (
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center select-none">
            <p className="text-sm tracking-[0.2em] text-blue-400/40 uppercase">
              Move your mouse
            </p>
          </div>
        )}
      </motion.div>
    </motion.div>
  )
}

export default SpotlightIntro
